import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus, Trash2 } from "lucide-react";

const NAVY = "#0B1F3A";
const RED  = "#7f1d1d";

const EMPTY_LINE = {
  description: "", customer_material_code: "", customer_drawing_number: "",
  cylinder_type: "Hydraulic", manufacturing_type: "New",
  bore_mm: "", rod_diameter_mm: "", stroke_mm: "", operating_pressure_bar: "",
  uom: "Nos", quantity: 1, delivery_date: "", unit_price: "", remarks: "",
};

const TERM_FIELDS = [
  { k: "price_basis",        label: "Price Basis" },
  { k: "packing_forwarding", label: "Packing & Forwarding (P&F)" },
  { k: "payment",            label: "Payment" },
  { k: "igst",               label: "IGST" },
  { k: "delivery_mode",      label: "Delivery Mode" },
  { k: "cgst",               label: "CGST" },
  { k: "inspection",         label: "Inspection" },
  { k: "sgst",               label: "SGST" },
  { k: "painting",           label: "Painting" },
  { k: "freight",            label: "Freight" },
  { k: "insurance",          label: "Insurance" },
];

export default function OaForm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const po_id = params.get("po_id");
  // Direct/historical mode: no PO in the URL. Customer is the only required
  // field; every other reference (Inquiry / Quotation / PO / DC / cylinder)
  // is optional and can be attached later. See HISPL rule:
  // "Missing document ≠ blocked workflow."
  const historicalMode = !po_id;

  const [po, setPo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!po_id);

  const [form, setForm] = useState({
    purchase_order_id: po_id || "",
    oa_date: new Date().toISOString().slice(0, 10),
    delivery_commitment: "",
    project_name: "",
    remarks: "",
    department: "",
    customer_id: "",
    plant_id: "",
    customer_address: "",
    customer_po_number: "",
    po_date: "",
    quotation_id: "",
    quotation_number: "",
    quotation_date: "",
    inquiry_id: "",
    inquiry_number: "",
    terms: {
      price_basis: "Ex-Works",
      packing_forwarding: "Included",
      payment: "",
      igst: "18%",
      delivery_mode: "By Road",
      cgst: "N/a",
      inspection: "At our works",
      sgst: "N/a",
      painting: "HISPL Standard Red",
      freight: "Extra at actuals",
      drawing_approval: "YES",
      insurance: "Not in our scope",
    },
    // Direct-mode OA can be created without any line items — user can add
    // them later. PO-linked mode pre-fills line items from the PO.
    line_items: po_id ? [{ ...EMPTY_LINE }] : [],
  });

  // Load PO (PO-linked mode)
  useEffect(() => {
    if (po_id) {
      api.get(`/purchase-orders-detail/${po_id}`).then((r) => {
        const p = r.data;
        setPo(p);
        setForm((f) => ({
          ...f,
          purchase_order_id: po_id,
          customer_id: p.customer_id,
          customer_po_number: p.customer_po_reference || p.po_number,
          po_date: p.po_date || "",
          quotation_id: p.quotation_id || "",
          quotation_number: p.quotation_number || "",
          line_items: (p.line_items || []).map((li, i) => ({
            ...EMPTY_LINE,
            description: li.description || "",
            customer_material_code: li.customer_material_code || "",
            customer_drawing_number: li.customer_drawing_number || "",
            cylinder_type: li.cylinder_type || "Hydraulic",
            manufacturing_type: li.manufacturing_type || "New",
            bore_mm: li.bore_mm ?? "",
            rod_diameter_mm: li.rod_diameter_mm ?? "",
            stroke_mm: li.stroke_mm ?? "",
            operating_pressure_bar: li.operating_pressure_bar ?? "",
            quantity: li.quantity || 1,
            unit_price: li.unit_price ?? "",
            uom: li.uom || "Nos",
          })),
        }));
      }).catch(() => toast.error("Failed to load PO"))
        .finally(() => setLoading(false));
    }
  }, [po_id]);

  // Load lookup lists in historical mode
  useEffect(() => {
    if (historicalMode) {
      api.get("/customers?limit=500").then((r) => setCustomers(r.data.items || r.data || []));
    }
  }, [historicalMode]);

  // When customer changes in historical mode, load their quotations/inquiries so user can link if available
  useEffect(() => {
    if (historicalMode && form.customer_id) {
      api.get(`/quotations?customer_id=${form.customer_id}&limit=200`).then((r) => setQuotations(r.data.items || r.data || [])).catch(() => setQuotations([]));
      api.get(`/inquiries?customer_id=${form.customer_id}&limit=200`).then((r) => setInquiries(r.data.items || r.data || [])).catch(() => setInquiries([]));
    }
  }, [historicalMode, form.customer_id]);

  const totalValue = useMemo(
    () => form.line_items.reduce((s, li) => s + (Number(li.unit_price || 0) * Number(li.quantity || 0)), 0),
    [form.line_items]
  );
  const totalQty = useMemo(
    () => form.line_items.reduce((s, li) => s + Number(li.quantity || 0), 0),
    [form.line_items]
  );

  function updateLine(idx, patch) {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  }
  function addLine() { setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] })); }
  function removeLine(idx) {
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  }
  function setTerm(k, v) {
    setForm((f) => ({ ...f, terms: { ...f.terms, [k]: v } }));
  }

  async function submit(e) {
    e.preventDefault();
    // MINIMAL guards. Missing references (Inquiry / Quotation / PO / DC /
    // cylinder / plant) must never block OA creation.
    if (historicalMode && !form.customer_id) { toast.error("Customer is required"); return; }
    if (!historicalMode && !form.purchase_order_id) { toast.error("PO link required"); return; }
    // Any lines that ARE present must be well-formed; empty lines list is OK.
    if (form.line_items.length && form.line_items.some((li) => !li.description || !li.quantity)) {
      toast.error("Each line item requires a description and quantity — remove empty lines or fill them in");
      return;
    }
    setSaving(true);
    try {
      // Coerce numerics
      const clean = {
        ...form,
        line_items: form.line_items.map((li) => ({
          ...li,
          bore_mm: li.bore_mm === "" ? null : Number(li.bore_mm),
          rod_diameter_mm: li.rod_diameter_mm === "" ? null : Number(li.rod_diameter_mm),
          stroke_mm: li.stroke_mm === "" ? null : Number(li.stroke_mm),
          operating_pressure_bar: li.operating_pressure_bar === "" ? null : Number(li.operating_pressure_bar),
          quantity: Number(li.quantity),
          unit_price: li.unit_price === "" ? null : Number(li.unit_price),
        })),
      };
      const { data } = await api.post("/order-acceptances", clean);
      toast.success(`Order Acceptance ${data.oa_number} created (Draft)`);
      nav(`/order-acceptances/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="oa-form-page" className="max-w-6xl space-y-5 pb-10">
      <div>
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: RED }}>
          {historicalMode ? "Direct Entry" : "From Purchase Order"}
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">
          {historicalMode ? "New Order Acceptance" : "New Order Acceptance"}
        </h1>
        {po && (
          <p className="text-sm text-slate-500 mt-1">
            From PO <span className="font-mono font-semibold">{po.po_number}</span> · {po.customer_name}
          </p>
        )}
        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
          <strong>Note:</strong> Only <em>Customer</em> is required. Inquiry, Quotation, Purchase Order, DC, line items and cylinder numbers may all be added later from the OA detail page. Cylinder Numbers are generated only when an OA is <em>Released to Production</em>.
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Customer & Reference Block */}
        <Card className="p-5" style={{ borderColor: NAVY, borderWidth: 1 }}>
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ color: NAVY }}>Header</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {historicalMode ? (
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Customer *</Label>
                <Select value={form.customer_id || ""} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="oa-customer"><SelectValue placeholder="Choose customer…" /></SelectTrigger>
                  <SelectContent>
                    {(customers || []).map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Customer</Label>
                <div className="mt-2 text-sm text-slate-900 font-medium">
                  {po?.customer_name
                    || (customers || []).find((c) => c.id === form.customer_id)?.name
                    || "—"}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Department</Label>
              <Input data-testid="oa-department" value={form.department}
                     onChange={(e) => setForm({ ...form, department: e.target.value })}
                     placeholder="e.g. Production / Mechanical Maintenance" className="mt-1" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer Address</Label>
              <Textarea rows={2} value={form.customer_address} data-testid="oa-cust-address"
                        onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">OA Date *</Label>
              <Input type="date" data-testid="oa-date" value={form.oa_date}
                     onChange={(e) => setForm({ ...form, oa_date: e.target.value })} className="mt-1" />
              <p className="text-[10px] text-slate-500 mt-1">OA number auto-generated on save.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Delivery Commitment</Label>
              <Input type="date" data-testid="oa-delivery" value={form.delivery_commitment}
                     onChange={(e) => setForm({ ...form, delivery_commitment: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Project Name</Label>
              <Input data-testid="oa-project" value={form.project_name}
                     onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="mt-1" />
            </div>
          </div>
        </Card>

        {/* Commercial References */}
        <Card className="p-5" style={{ borderColor: NAVY, borderWidth: 1 }}>
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ color: NAVY }}>Commercial References</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer PO Number{historicalMode ? "" : " *"}</Label>
              <Input data-testid="oa-po-number" value={form.customer_po_number}
                     onChange={(e) => setForm({ ...form, customer_po_number: e.target.value })}
                     className="mt-1 font-mono" readOnly={!historicalMode} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">PO Date</Label>
              <Input type="date" data-testid="oa-po-date" value={form.po_date}
                     onChange={(e) => setForm({ ...form, po_date: e.target.value })} className="mt-1" />
            </div>
            <div />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Quotation Number {historicalMode && <span className="text-[10px] text-slate-400">(optional)</span>}
              </Label>
              {historicalMode && quotations.length > 0 ? (
                <Select value={form.quotation_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setForm({ ...form, quotation_id: "", quotation_number: "", quotation_date: "" }); return; }
                  const q = quotations.find((x) => x.id === v);
                  setForm({ ...form, quotation_id: v, quotation_number: q?.quotation_number || "", quotation_date: q?.quotation_date || "" });
                }}>
                  <SelectTrigger className="mt-1" data-testid="oa-qtn"><SelectValue placeholder="Link a quotation…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {quotations.map((q) => (<SelectItem key={q.id} value={q.id}>{q.quotation_number} · {q.quotation_date}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid="oa-qtn-manual" value={form.quotation_number}
                       onChange={(e) => setForm({ ...form, quotation_number: e.target.value })}
                       className="mt-1 font-mono" />
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Quotation Date</Label>
              <Input type="date" data-testid="oa-qtn-date" value={form.quotation_date}
                     onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Inquiry Number <span className="text-[10px] text-slate-400">(optional)</span>
              </Label>
              {historicalMode && inquiries.length > 0 ? (
                <Select value={form.inquiry_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setForm({ ...form, inquiry_id: "", inquiry_number: "" }); return; }
                  const i = inquiries.find((x) => x.id === v);
                  setForm({ ...form, inquiry_id: v, inquiry_number: i?.inquiry_number || "" });
                }}>
                  <SelectTrigger className="mt-1" data-testid="oa-inq"><SelectValue placeholder="Link an inquiry…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {inquiries.map((i) => (<SelectItem key={i.id} value={i.id}>{i.inquiry_number} · {i.inquiry_date}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid="oa-inq-manual" value={form.inquiry_number}
                       onChange={(e) => setForm({ ...form, inquiry_number: e.target.value })}
                       className="mt-1 font-mono" />
              )}
            </div>
          </div>
        </Card>

        {/* Line Items */}
        <Card>
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900" style={{ color: NAVY }}>Line Items ({form.line_items.length})</div>
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="oa-add-line">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
            </Button>
          </div>
          <div className="p-3 space-y-3">
            {form.line_items.map((li, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid={`oa-line-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Line {i + 1}</div>
                  {form.line_items.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(i)}
                            className="h-6 text-red-600 hover:text-red-700" data-testid={`oa-remove-line-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Description *</Label>
                    <Input value={li.description} data-testid={`oa-line-desc-${i}`}
                           onChange={(e) => updateLine(i, { description: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Material Code</Label>
                    <Input value={li.customer_material_code} onChange={(e) => updateLine(i, { customer_material_code: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Drawing #</Label>
                    <Input value={li.customer_drawing_number} onChange={(e) => updateLine(i, { customer_drawing_number: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Cyl Type</Label>
                    <Input value={li.cylinder_type} onChange={(e) => updateLine(i, { cylinder_type: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">UOM</Label>
                    <Input value={li.uom} onChange={(e) => updateLine(i, { uom: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Bore mm</Label>
                    <Input type="number" value={li.bore_mm} onChange={(e) => updateLine(i, { bore_mm: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Rod Ø mm</Label>
                    <Input type="number" value={li.rod_diameter_mm} onChange={(e) => updateLine(i, { rod_diameter_mm: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Stroke mm</Label>
                    <Input type="number" value={li.stroke_mm} onChange={(e) => updateLine(i, { stroke_mm: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Pressure bar</Label>
                    <Input type="number" value={li.operating_pressure_bar} onChange={(e) => updateLine(i, { operating_pressure_bar: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Qty *</Label>
                    <Input type="number" min="1" value={li.quantity} data-testid={`oa-line-qty-${i}`}
                           onChange={(e) => updateLine(i, { quantity: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Unit Price</Label>
                    <Input type="number" step="0.01" value={li.unit_price}
                           onChange={(e) => updateLine(i, { unit_price: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Del. Date</Label>
                    <Input type="date" value={li.delivery_date} onChange={(e) => updateLine(i, { delivery_date: e.target.value })} className="mt-0.5 h-8 text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between text-sm">
            <div className="text-slate-600">Total Qty: <span className="font-mono font-bold" data-testid="oa-total-qty">{totalQty}</span></div>
            <div className="text-slate-600">Order Value: <span className="font-mono font-bold" data-testid="oa-total-value">₹{totalValue.toLocaleString("en-IN")}</span></div>
          </div>
        </Card>

        {/* Terms & Conditions */}
        <Card className="p-5" style={{ borderColor: NAVY, borderWidth: 1 }}>
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ color: NAVY }}>Terms & Conditions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TERM_FIELDS.map((t) => (
              <div key={t.k}>
                <Label className="text-xs uppercase tracking-wider text-slate-600">{t.label}</Label>
                <Input value={form.terms[t.k] || ""} data-testid={`oa-terms-${t.k}`}
                       onChange={(e) => setTerm(t.k, e.target.value)} className="mt-1" />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Drawing Approval</Label>
              <Select value={form.terms.drawing_approval || "YES"} onValueChange={(v) => setTerm("drawing_approval", v)}>
                <SelectTrigger className="mt-1" data-testid="oa-terms-drawing_approval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="YES">YES</SelectItem>
                  <SelectItem value="NO">NO</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks</Label>
          <Textarea rows={2} value={form.remarks} data-testid="oa-remarks"
                    onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" />
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" data-testid="oa-submit" disabled={saving}
                  style={{ backgroundColor: RED, color: "white" }}
                  className="hover:opacity-90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Order Acceptance"}
          </Button>
        </div>
      </form>
    </div>
  );
}
